import { useState } from 'react';
import clsx from 'clsx';
import { rondReparatieInHuisAf, type ReparatieOpdracht } from '../../lib/reparatie';

interface Props {
  opdracht: ReparatieOpdracht;
  onClose: () => void;
  onDone: () => void;
}

/**
 * Reparatie in Havelte afronden. Eén handeling in plaats van gebracht, klaar
 * en opgehaald: het kleed verlaat het pand niet, dus er hoort geen rit en
 * geen statuswissel bij. De bevestiging is de kern (server bewaakt dat ook):
 * afronden geeft het kleed vrij voor aflevering.
 */
export function ReparatieInHuisAfrondenDialog({ opdracht, onClose, onDone }: Props) {
  const [bevestiging, setBevestiging] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSubmit = !busy && bevestiging.trim().length >= 5;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      const res = await rondReparatieInHuisAf({ opdrachtId: opdracht.id, bevestiging: bevestiging.trim() });
      if (!res.success) {
        setError(res.message || 'Afronden mislukt');
        setBusy(false);
        return;
      }
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Afronden mislukt');
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="text-base font-semibold text-slate-900">Reparatie afronden</h2>
          <p className="text-xs text-slate-500 mt-1">{opdracht.opdrachtNummer} · reparatie in Havelte</p>
        </div>
        <div className="px-6 py-4 space-y-4">
          <p className="text-sm text-slate-700">
            Rond alleen af als het werk echt gedaan is. Het kleed blijft staan waar het staat en gaat
            mee met de aflevering; er wordt geen rit gepland of geannuleerd.
          </p>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">
              Wie in de wasserij bevestigde dit? (verplicht)
            </label>
            <textarea
              className="w-full text-sm border border-slate-300 rounded px-2 py-1.5"
              rows={3}
              placeholder="Bijv. Dinand, 5 nov: kleed is verstevigd en ligt ingepakt in het schap."
              value={bevestiging}
              onChange={(e) => setBevestiging(e.target.value)}
              disabled={busy}
            />
          </div>
          {error && <p className="text-xs text-red-600">{error}</p>}
        </div>
        <div className="px-6 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="text-sm text-slate-600 hover:text-slate-900 px-3 py-1.5"
          >
            Annuleren
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className={clsx(
              'text-sm font-medium px-3 py-1.5 rounded',
              canSubmit
                ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                : 'bg-slate-200 text-slate-500 cursor-not-allowed'
            )}
          >
            {busy ? 'Bezig…' : 'Reparatie afgerond'}
          </button>
        </div>
      </div>
    </div>
  );
}
